import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Truck, ShieldCheck, Sparkles, Clock, Star, Wind, Package, MapPin } from "lucide-react";
import Navbar from "@/components/quickwash/Navbar";
import Footer from "@/components/quickwash/Footer";
import { BRAND } from "@/lib/api";

const features = [
  { icon: Wind, title: "Steam Ironed", desc: "Crisp finish every time with professional steam pressing." },
  { icon: Sparkles, title: "Premium Dry Cleaning", desc: "Suits, sarees, blazers, blankets — handled with care." },
  { icon: Truck, title: "Free Pickup & Delivery", desc: "Doorstep service across Rohtak, 7 days a week." },
  { icon: Clock, title: "48-Hour Turnaround", desc: "Fast, reliable service without cutting corners." },
  { icon: ShieldCheck, title: "Zero Damage Guarantee", desc: "Your fabrics, our responsibility. Fully insured cleaning." },
  { icon: Package, title: "Neatly Packed", desc: "Delivered folded, hangered and beautifully packed." },
];

const reviews = [
  { name: "Rahul Sharma", city: "Rohtak", text: "Best laundry service in Rohtak. My suits come back looking brand new every single time.", stars: 5 },
  { name: "Neha Yadav", city: "Sector 4", text: "Super convenient pickup and delivery. The WhatsApp updates are a game-changer!", stars: 5 },
  { name: "Amit Malik", city: "Sector 6", text: "Very fair pricing and clothes are always well-ironed. Highly recommended.", stars: 5 },
];

const Home = () => {
  const waLink = `https://wa.me/${BRAND.waPhone}?text=${encodeURIComponent("Hi Quick Wash, I would like to book a pickup")}`;

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="qw-container pt-14 pb-16 lg:pt-24 lg:pb-24 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-semibold text-slate-600 mb-6" data-testid="hero-badge">
            <Sparkles size={14} className="text-[#F97316]"/> Rohtak's #1 Premium Laundry Service
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="hero-title">
            Laundry that feels <span className="text-[#2563EB]">brand new</span>, every time.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="hero-subtitle">
            Wash & fold, dry cleaning, steam ironing, blankets, curtains and shoe cleaning — with free pickup & doorstep delivery across Rohtak.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
            <Link to="/book" className="qw-btn-primary" data-testid="cta-book-pickup"><Truck size={18}/>Book Free Pickup</Link>
            <a href={`tel:${BRAND.phone}`} className="qw-btn-outline" data-testid="cta-call-now"><Phone size={18}/>Call Now</a>
            <a href={waLink} target="_blank" rel="noreferrer" className="qw-btn-wa" data-testid="cta-whatsapp"><MessageCircle size={18}/>WhatsApp</a>
            <Link to="/login" className="qw-btn-ghost" data-testid="cta-billing-login">Billing Login →</Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1.5"><Star size={16} className="fill-orange-400 text-orange-400"/><span className="font-semibold text-slate-800">4.9</span> / 5 · 1,200+ orders</div>
            <div className="flex items-center gap-1.5"><MapPin size={16}/>Sec. 4 Extn, Rohtak</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }} className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(37,99,235,0.25)] bg-white">
            <img src="/quickwash-brand.jpg" alt="Quick Wash Brand Pamphlet" className="w-full h-[420px] lg:h-[560px] object-contain bg-white"/>
          </div>
          <div className="absolute -bottom-6 -left-6 hidden md:block qw-card p-5 w-64 animate-float">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Today's Pickups</div>
            <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Poppins' }}>48</div>
            <div className="text-xs text-emerald-600 font-semibold">+12% from yesterday</div>
          </div>
          <div className="absolute -top-6 -right-6 hidden md:block qw-card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600"><ShieldCheck size={18}/></div>
            <div className="text-xs">
              <div className="font-semibold text-slate-900">Zero Damage</div>
              <div className="text-slate-500">100% Fabric Safe</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="qw-container qw-section" data-testid="features-section">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">Why Quick Wash</div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Premium care, without the premium hassle.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="qw-card qw-card-hover p-7">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#2563EB] flex items-center justify-center mb-4"><f.icon size={22}/></div>
              <div className="font-semibold text-lg text-slate-900 mb-1" style={{ fontFamily: 'Poppins' }}>{f.title}</div>
              <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="qw-container qw-section grid lg:grid-cols-2 gap-12 items-center">
        <img src="https://images.pexels.com/photos/4440566/pexels-photo-4440566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Folded clothes" className="rounded-3xl shadow-xl w-full h-[420px] object-cover"/>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F97316] mb-3">Trusted by 1000+ families</div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>Rohtak's most reliable laundry, at your doorstep.</h2>
          <p className="text-slate-600 leading-relaxed mb-6">From delicate silk sarees to heavy woolen blankets, we handle every fabric with the care it deserves. Transparent pricing, honest turnaround, and real people you can call.</p>
          <ul className="space-y-3">
            {["Free pickup & delivery across Rohtak", "Live order tracking via WhatsApp", "Transparent per-item pricing in ₹", "Damage-free guarantee on every garment"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700"><span className="mt-1 w-2 h-2 rounded-full bg-emerald-500"/> {t}</li>
            ))}
          </ul>
          <Link to="/book" className="qw-btn-primary mt-8" data-testid="why-book-btn">Book Your First Pickup</Link>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-slate-900/[.02] border-y border-slate-100 qw-section" data-testid="reviews-section">
        <div className="qw-container">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#10B981] mb-3">Customer Reviews</div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-10" style={{ fontFamily: 'Poppins' }}>What our customers say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <div key={r.name} className="qw-card p-7">
                <div className="flex mb-3">{Array.from({ length: r.stars }).map((_, i) => <Star key={i} size={16} className="fill-orange-400 text-orange-400"/>)}</div>
                <p className="text-slate-700 leading-relaxed mb-5">"{r.text}"</p>
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-slate-500">{r.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="qw-container qw-section">
        <div className="rounded-3xl bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="max-w-2xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Poppins' }}>Ready for cleaner, crisper clothes?</h2>
            <p className="text-blue-100 mb-6">Book a free pickup today. We'll wash, iron and deliver — while you focus on what matters.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/book" className="qw-btn-accent" data-testid="cta-band-book">Book Free Pickup</Link>
              <a href={`tel:${BRAND.phone}`} className="qw-btn-ghost bg-white/10 text-white hover:bg-white/20"><Phone size={16}/> Call {BRAND.phone}</a>
            </div>
          </div>
          <div className="absolute -right-10 -bottom-10 w-72 h-72 rounded-full bg-white/10"/>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
